import { ReactNode, useMemo, useState } from 'react';
import Layout from '@/layouts/Layout';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Link, router } from '@inertiajs/react';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/date';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DemoPatientBanner from './DemoPatientBanner';
import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  BookOpen,
  FileText,
  MessageSquare,
  Target,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EmoviDocPanel, EmoviDocProvider, EmoviDocToggleButton } from './emovidoc';

type PatientLayoutProps = {
  patient: {
    id: number;
    name: string;
    email?: string | null;
    demo?: boolean;
    emovidocEnabled?: boolean;
  };
  stats: {
    activityRate: number;
    totalChats: number;
    totalDiaries: number;
    lastActivityAt: string | null;
  };
  charts: { moodTrend: Array<{ date: string; mood: number | null }> };
  activeTab: 'dashboard' | 'chat' | 'diary' | 'notes' | 'profile';
  showWeeklyReviewCta?: boolean;
  children: ReactNode;
};

type ReviewPreset = 'last_7_days' | 'last_14_days' | 'custom';

const tabs = (id: number) => [
  {
    key: 'dashboard',
    label: 'Dashboard',
    href: `/therapists/patients/${id}`,
    icon: <TrendingUp className="h-4 w-4" />,
  },
  {
    key: 'chat',
    label: 'Chat',
    href: `/therapists/patients/${id}/chats`,
    icon: <MessageSquare className="h-4 w-4" />,
  },
  {
    key: 'diary',
    label: 'Diari',
    href: `/therapists/patients/${id}/journals`,
    icon: <BookOpen className="h-4 w-4" />,
  },
  {
    key: 'notes',
    label: 'Note cliniche',
    href: `/therapists/patients/${id}/notes`,
    icon: <FileText className="h-4 w-4" />,
  },
  {
    key: 'profile',
    label: 'Profilo',
    href: `/therapists/patients/${id}/profile`,
    icon: <FileText className="h-4 w-4" />,
  },
];

function initials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function currentMood(charts: PatientLayoutProps['charts']) {
  const last = [...charts.moodTrend].reverse().find((p) => p.mood !== null && p.mood !== undefined);
  return last?.mood ?? null;
}

function moodColor(mood: number | null) {
  if (mood === null) return 'text-muted-foreground';
  if (mood >= 7) return 'text-green-600 dark:text-green-400';
  if (mood >= 5) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

export default function PatientLayout({
  patient,
  stats,
  charts,
  activeTab,
  showWeeklyReviewCta = true,
  children,
}: PatientLayoutProps) {
  const mood = currentMood(charts);
  const handleDeleteDemo = () =>
    router.delete(`/therapists/patients/${patient.id}`, { preserveScroll: true });
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewPreset, setReviewPreset] = useState<ReviewPreset>('last_7_days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [reviewError, setReviewError] = useState('');

  const isCustomRange = reviewPreset === 'custom';

  const parsedCustomRange = useMemo(() => {
    if (!customStart || !customEnd) return null;
    const startParts = customStart.split('-').map(Number);
    const endParts = customEnd.split('-').map(Number);
    if (startParts.length !== 3 || endParts.length !== 3) return null;
    const [startYear, startMonth, startDay] = startParts;
    const [endYear, endMonth, endDay] = endParts;
    if (!startYear || !startMonth || !startDay || !endYear || !endMonth || !endDay) return null;
    const startDate = new Date(Date.UTC(startYear, startMonth - 1, startDay));
    const endDate = new Date(Date.UTC(endYear, endMonth - 1, endDay));
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
    return { startDate, endDate };
  }, [customEnd, customStart]);

  const validateCustomRange = () => {
    if (!customStart || !customEnd) return 'Seleziona entrambe le date.';
    if (!parsedCustomRange) return 'Date non valide.';
    if (parsedCustomRange.startDate > parsedCustomRange.endDate) {
      return "Intervallo date non valido.";
    }
    const diffMs = parsedCustomRange.endDate.getTime() - parsedCustomRange.startDate.getTime();
    const diffDays = Math.floor(diffMs / 86_400_000) + 1;
    if (diffDays > 30) return 'Intervallo troppo ampio (max 30 giorni).';
    return '';
  };

  const handleReviewPresetChange = (value: ReviewPreset) => {
    setReviewPreset(value);
    setReviewError('');
  };

  const handleOpenReview = () => {
    if (isCustomRange) {
      const error = validateCustomRange();
      if (error) {
        setReviewError(error);
        return;
      }
    }

    const params = new URLSearchParams({ preset: reviewPreset });
    if (isCustomRange) {
      params.set('window_start', customStart);
      params.set('window_end', customEnd);
    }
    setReviewDialogOpen(false);
    router.visit(`/therapists/patients/${patient.id}/weekly_review?${params.toString()}`);
  };

  return (
    <EmoviDocProvider>
      <Layout isAdmin>
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                <div className="flex items-start gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                      {initials(patient.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <h1 className="text-lg font-semibold leading-none">{patient.name}</h1>
                      <Badge variant="outline">ID {patient.id}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      {patient.email && <span>{patient.email}</span>}
                      {stats.lastActivityAt && (
                        <span>Ultima attività: {formatDateTime(stats.lastActivityAt)}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {patient.emovidocEnabled ? <EmoviDocToggleButton /> : null}
                  <Link href={`/therapists/patients/${patient.id}/notes`} className="inline-flex">
                    <Button size="sm">Aggiungi nota</Button>
                  </Link>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatusTile
                  icon={<Activity className="h-5 w-5" />}
                  label="Umore attuale"
                  value={mood !== null ? `${mood}/10` : 'N/A'}
                  valueClass={moodColor(mood)}
                  trendIcon={(() => {
                    const prevMood =
                      charts.moodTrend.length > 1
                        ? charts.moodTrend[charts.moodTrend.length - 2]?.mood
                        : mood;
                    if (mood === null || prevMood === undefined || prevMood === null)
                      return <TrendingUp className="h-4 w-4" />;
                    return mood < prevMood ? (
                      <TrendingDown className="h-4 w-4" />
                    ) : (
                      <TrendingUp className="h-4 w-4" />
                    );
                  })()}
                />
                <StatusTile
                  icon={<Target className="h-5 w-5 text-primary" />}
                  label="Presenza"
                  value={`${stats.activityRate}%`}
                  trendIcon={
                    stats.activityRate >= 50 ? (
                      <ArrowUpRight className="h-4 w-4 text-green-600" />
                    ) : (
                      <ArrowDownRight className="h-4 w-4 text-red-600" />
                    )
                  }
                />
                <StatusTile
                  icon={<MessageSquare className="h-5 w-5 text-sky-600" />}
                  label="Chat totali"
                  value={stats.totalChats}
                />
                <StatusTile
                  icon={<BookOpen className="h-5 w-5 text-emerald-600" />}
                  label="Diari"
                  value={stats.totalDiaries}
                />
              </div>
            </CardContent>
          </Card>

          {patient.demo ? <DemoPatientBanner onDelete={handleDeleteDemo} /> : null}

          <Tabs value={activeTab} className="w-full">
            <TabsList className="w-full justify-start">
              {tabs(patient.id).map((tab) => (
                <TabsTrigger key={tab.key} value={tab.key} asChild>
                  <Link href={tab.href} className="gap-2">
                    {tab.icon}
                    {tab.label}
                  </Link>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {showWeeklyReviewCta && activeTab === 'dashboard' ? (
            <Card variant="elevated">
              <CardHeader>
                <CardTitle>Com&apos;è andato il periodo</CardTitle>
                <CardAction>
                  <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline">
                        Apri review
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Seleziona periodo</DialogTitle>
                        <DialogDescription>
                          Scegli la finestra temporale per la review clinica.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="weekly-review-preset">Periodo</Label>
                          <Select
                            value={reviewPreset}
                            onValueChange={(value) => handleReviewPresetChange(value as ReviewPreset)}
                          >
                            <SelectTrigger id="weekly-review-preset">
                              <SelectValue placeholder="Seleziona periodo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="last_7_days">Ultimi 7 giorni</SelectItem>
                              <SelectItem value="last_14_days">Ultimi 14 giorni</SelectItem>
                              <SelectItem value="custom">Personalizzato</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {isCustomRange ? (
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="weekly-review-start">Da</Label>
                              <Input
                                id="weekly-review-start"
                                type="date"
                                value={customStart}
                                onChange={(event) => setCustomStart(event.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="weekly-review-end">A</Label>
                              <Input
                                id="weekly-review-end"
                                type="date"
                                value={customEnd}
                                onChange={(event) => setCustomEnd(event.target.value)}
                              />
                            </div>
                          </div>
                        ) : null}

                        {reviewError ? (
                          <p className="text-sm text-destructive">{reviewError}</p>
                        ) : null}
                      </div>
                      <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                          Annulla
                        </Button>
                        <Button onClick={handleOpenReview}>Genera review</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardAction>
              </CardHeader>
              <CardContent>Genera un report clinico sintetico sul periodo selezionato.</CardContent>
            </Card>
          ) : null}

          {children}
        </div>
      </Layout>

      {patient.emovidocEnabled ? <EmoviDocPanel patientId={patient.id} /> : null}
    </EmoviDocProvider>
  );
}

function StatusTile({
  icon,
  label,
  value,
  valueClass,
  trendIcon,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
  valueClass?: string;
  trendIcon?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
        {icon}
      </div>
      <div className="space-y-0.5">
        <p className="text-sm text-muted-foreground">{label}</p>
        <div className={cn('font-semibold flex items-center gap-1', valueClass)}>
          {value}
          {trendIcon}
        </div>
      </div>
    </div>
  );
}
